/**
 * Lands here after a Microsoft/Zoho redirect (/oauth/callback/:provider).
 * Reads the one-time `code` off the URL, matches it against what was stashed
 * in sessionStorage before the redirect (CSRF `state` check + which login
 * page/context started this), exchanges it with the backend, then bounces
 * back to that same login page with the result in router state so its
 * existing completion logic (role picker / vendor checks / studio checks)
 * handles it exactly like the same-page Google flow does.
 */
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';

// Module-level (not component-level) guard: React 18 StrictMode's dev-only
// mount->unmount->remount cycle gives this component a FRESH useRef on the
// second mount, so a ref alone can't stop it from re-running. A module-level
// variable survives that remount since it isn't tied to the component
// instance, and it's keyed by the URL's own code+state (stable across both
// mounts) rather than sessionStorage (which the first run already clears) —
// without this, the second run sees an empty sessionStorage, treats it as
// "expired," and bounces to /login while the first run's real exchange is
// still in flight.
let handledKey = null;

const OAuthCallback = () => {
  const { provider } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error_description') || params.get('error');
    const key = `${provider}:${code}:${state}`;

    if (handledKey === key) return;
    handledKey = key;

    (async () => {
      const raw = sessionStorage.getItem('oauth_pending');
      sessionStorage.removeItem('oauth_pending');
      let pending = null;
      try { pending = raw ? JSON.parse(raw) : null; } catch { pending = null; }
      const fallbackPath = pending?.loginPath || '/login';

      if (error || !code || !pending || pending.provider !== provider || pending.state !== state) {
        toast.error('Sign-in was cancelled or the link expired. Please try again.');
        navigate(fallbackPath, { replace: true });
        return;
      }

      try {
        const endpoint = pending.context === 'services' || pending.context === 'credit'
          ? `/vendor/auth/${provider}`
          : `/auth/${provider}`;
        const response = await api.post(endpoint, {
          code,
          code_verifier: pending.codeVerifier,
          redirect_uri: pending.redirectUri,
        });
        navigate(fallbackPath, { replace: true, state: { oauthResult: response.data, oauthProvider: provider } });
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Sign-in failed');
        navigate(fallbackPath, { replace: true });
      }
    })();
  }, [provider, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-text-secondary text-sm">
      Signing you in…
    </div>
  );
};

export default OAuthCallback;
